/**
 * Blok Over in Instellingen > Meer. L6 van blok L (Frank, 19 oktober 2026).
 *
 * De versie van deze bouw (de bestaande `BuildDetails`, app en backend apart) en de
 * SBOM die de bouwstap in de app legt (`desktop/scripts/sbom.js`, CycloneDX 1.5).
 *
 * EEN ONBEKEND PAD KRIJGT index.html TERUG MET STATUS 200 (`services/spa.py`). Een
 * bouw zonder SBOM geeft dus geen 404 maar een HTML-pagina. Het blok toont daarom
 * alleen een SBOM als het antwoord er echt een is (`isSbom`), en zegt anders dat
 * deze bouw er geen heeft.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useTaal } from "@/contexts/TaalContext";
import { BuildDetails } from "./BuildStamp";
import { VERSION } from "../build-info";
import Beeldmerk from "./Beeldmerk";


export const SBOM_PAD = "/sbom.json";

/** Of dit antwoord een CycloneDX-SBOM is. PUUR. */
export function isSbom(data) {
  return Boolean(data) && typeof data === "object" && data.bomFormat === "CycloneDX"
    && Array.isArray(data.components);
}

/** Het aantal componenten per bronbestand. PUUR. */
export function perBron(sbom) {
  const uit = {};
  for (const c of sbom.components) {
    for (const p of c.properties || []) {
      if (p.name === "novamund:bron") uit[p.value] = (uit[p.value] || 0) + 1;
    }
  }
  return uit;
}

export default function OverBlok() {
  const { t, taal } = useTaal();
  const [sbom, setSbom] = useState(undefined);

  useEffect(() => {
    let weg = false;
    axios.get(SBOM_PAD, { baseURL: "" })
      .then((r) => { if (!weg) setSbom(isSbom(r?.data) ? r.data : null); })
      .catch(() => { if (!weg) setSbom(null); });
    return () => { weg = true; };
  }, []);

  const sbomVersie = sbom?.metadata?.component?.version;
  const tijd = sbom?.metadata?.timestamp ? new Date(sbom.metadata.timestamp) : null;
  return (
    <div className="pt-3 border-t space-y-2" data-testid="over-blok">
      <p className="text-sm font-medium">{t("Over")}</p>
      {/* F3b (Frank, 20 oktober 2026), vaste regel: hier staat altijd het NovaMund-
          beeldmerk en dat het een product van NovaMund is, ook bij een eigen huisstijl. */}
      <div className="flex items-center gap-2" data-testid="over-novamund">
        <Beeldmerk className="h-6 w-6 shrink-0" testId="over-beeldmerk" />
        <span className="text-xs">{t("NovaMund. Een eigen huisstijl verandert de naam en de kleur, niet het product.")}</span>
      </div>
      <p className="text-xs font-medium">{t("Deze versie")}</p>
      <BuildDetails />
      <div className="text-xs space-y-1 pt-2" data-testid="over-sbom">
        <p className="font-medium">SBOM</p>
        {sbom === undefined && <p className="text-muted-foreground">{t("Laden...")}</p>}
        {sbom === null && (
          <p className="text-muted-foreground" data-testid="over-sbom-geen">
            {t("Deze bouw heeft geen SBOM. Die maakt de bouwstap bij het bouwen van de app.")}
          </p>
        )}
        {sbom && (
          <>
            <p data-testid="over-sbom-samen">
              CycloneDX {sbom.specVersion} · {sbom.components.length} {t("componenten")}
              {tijd && !Number.isNaN(tijd.getTime()) ? ` · ${tijd.toLocaleString(taal === "en" ? "en-GB" : "nl-NL")}` : ""}
            </p>
            {Object.entries(perBron(sbom)).map(([bron, n]) => (
              <p key={bron} className="text-muted-foreground font-mono">{bron}: {n}</p>
            ))}
            {sbomVersie && sbomVersie !== VERSION && (
              <p className="text-destructive" data-testid="over-sbom-andere-versie">
                {t("Deze SBOM hoort bij versie")} {sbomVersie}.
              </p>
            )}
            <a href={SBOM_PAD} download={`novamund-sbom-${sbomVersie || VERSION}.json`}
               className="text-[hsl(var(--accent))] hover:underline" data-testid="over-sbom-download">
              {t("SBOM downloaden")}
            </a>
          </>
        )}
      </div>
    </div>
  );
}
